import React, { useState } from "react";
import { Check, Copy, Instagram, Twitter, Facebook, Send } from "lucide-react";
import {
  hasPositiveBalance,
  truncateDecimals,
} from "../services/Web3Services";
import logo from "../media/logo.png";
import cv_token from "../media/cv_token.png";
import cv_token2 from "../media/26566.png";
import homepage_logo from "../media/homepage_logo.jpeg";

// Reusable components (no changes needed here)
export const CVSwapLogo = () => (
  <div className="flex items-center gap-2">
    <img src={logo} alt="CVSwap Logo" className="h-6 w-auto object-contain" />
  </div>
);
export const CVTokenLogo2 = () => (
  <div className="flex items-center gap-2">
    <img
      src={cv_token2}
      alt="CVToken Logo"
      className="h-6 w-auto object-contain"
    />
    <span className="text-xl font-bold text-white">CVT V1</span>
  </div>
);

export interface HomePageProps {
  onConnectWallet: () => Promise<void>;
  isConnecting: boolean;
  account: string;
  /** null means the balance could not be read — not a zero balance. */
  cvt1Balance: string | null;
  onRetryBalance?: () => Promise<void>;
  onDeposit: () => Promise<void>; // Amount is handled in the App component
  isDepositing: boolean;
  statusMessage: { type: "success" | "error"; text: string } | null;
}

const HomePage: React.FC<HomePageProps> = ({
  onConnectWallet,
  isConnecting,
  account,
  cvt1Balance,
  onRetryBalance,
  onDeposit,
  isDepositing,
  statusMessage,
}) => {
  const [copied, setCopied] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const balanceFailed = cvt1Balance === null;

  const handleRetry = async () => {
    if (!onRetryBalance) return;
    setIsRetrying(true);
    try {
      await onRetryBalance();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDeposit = async () => {
    if (!hasPositiveBalance(cvt1Balance)) {
      console.error("No balance to deposit");
      return;
    }
    await onDeposit();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderContent = () => {
    // If wallet is not connected, show the connect button
    if (!account) {
      return (
        <div
          className="min-h-screen bg-black text-white font-sans flex flex-col p-6"
          style={{
            background: `radial-gradient(
            1200px 700px at top left,
          rgb(2, 36, 19),
          rgb(0, 2, 5) 30%
           )`,
          }}
        >
          {/* Header with Logo */}
          <header className="w-full mb-8">
            <CVSwapLogo />
          </header>

          {/* Main Content Area */}
          <main className="flex-grow flex flex-col justify-center">
            <div className="w-full max-w-md mx-auto">
              {/* Text Content */}
              <div className="mb-10 text-center">
                <h1 className="text-3xl font-extrabold text-white leading-tight mb-3">
                  Swap your <span className="text-[#8BE68B]">CV</span>
                  <br /> Tokens.
                </h1>
                <p className="text-gray-400 text-sm font-sora font-semibold">
                  This DApp lets CVT V1 holders swap their tokens for CVT V2.
                  Simply connect your wallet, deposit your CVT V1, and receive
                  CVT V2 directly through the app.
                </p>
              </div>

              {/* Connect Wallet Button */}
              <div className="mb-6 flex justify-center">
                <button
                  onClick={onConnectWallet}
                  disabled={isConnecting}
                  className="bg-gradient-to-r from-[#00FF7F] to-[#00D06C] text-black font-bold py-3 px-8 rounded-lg shadow-lg disabled:opacity-70 flex items-center justify-center hover:opacity-90 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(to right,rgba(59, 177, 78, 0.98),rgba(80, 177, 96, 0.98))",
                  }}
                >
                  {isConnecting ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                  ) : (
                    <span>Connect Wallet</span>
                  )}
                </button>
              </div>

              {/* Centered CV Token Logo */}
              <div className="flex justify-center">
                <img
                  src={cv_token}
                  alt="CV Token"
                  className="w-55 h-55 object-contain transform transition duration-300 hover:scale-105"
                />
              </div>
            </div>
          </main>

          {/* Footer with Social Icons */}
          <footer className="w-full flex justify-center items-center mt-8">
            <div className="bg-[#222221] rounded-full px-6 py-3 flex items-center gap-6 text-white shadow-sm">
              <a
                href="https://x.com/CvTradeExchange"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-300 transition-colors duration-200"
              >
                <Twitter size={20} />
              </a>
              <a
                href="https://instagram.com/officialcvtrade"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-300 transition-colors duration-200"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://t.me/cvtradeexchange"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-300 transition-colors duration-200"
              >
                <Send size={20} />
              </a>
              <a
                href="https://facebook.com/cvtrade"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-300 transition-colors duration-200"
              >
                <Facebook size={20} />
              </a>
            </div>
          </footer>
        </div>
      );
    }

    // If wallet is connected, show the deposit interface
    return (
      <div
        className="min-h-screen text-white font-sans flex flex-col p-6 relative overflow-hidden"
        style={{
          background: `radial-gradient(
          1200px 700px at top left,
         rgb(2, 36, 19),
         rgb(0, 2, 5) 30%
          )`,
        }}
      >
        {/* Header with Logo and Token */}
        <header className="w-full flex justify-between items-center mb-8">
          <CVSwapLogo />
          <div className="bg-[#2E2E2E] rounded px-4 py-2 shadow-md">
            <p className="text-gray-200 text-sm font-mono flex items-center gap-2">
              {account.substring(0, 6)}...
              {account.substring(account.length - 4)}
              <button onClick={() => copyToClipboard(account)}>
                {copied ? (
                  <Check size={16} className="text-green-400" />
                ) : (
                  <Copy size={16} className="text-gray-500 hover:text-white" />
                )}
              </button>
            </p>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-grow flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            {/* Swap graphic. The source JPEG has a hard black background, which
                shows as a visible rectangle against the green-tinted gradient.
                `screen` blending drops pure black to transparent so the artwork
                sits on the page background instead of its own black box. */}
            <img
              src={homepage_logo}
              alt="CVT V1 to CVT V2 swap"
              className="w-full max-w-[260px] h-auto mx-auto mb-4 mix-blend-screen"
            />

            {/* Headline and Wallet */}
            <div className="flex justify-center items-center mb-6">
              <h1 className="text-4xl font-extrabold text-white leading-tight text-center">
                Swap your <span className="text-[#8BE68B]">CV</span> Tokens.
              </h1>
            </div>

            {/* Description */}
            <p className="text-gray-400 text-[15px] leading-relaxed mb-8 max-w-md mx-auto text-center font-sora font-semibold">
              This DApp lets CVT V1 holders swap their tokens for CVT V2. Simply
              connect your wallet, deposit your CVT V1, and receive CVT V2
              directly through the app.
            </p>

            {/* Deposit Box */}
            <div className="bg-[#1A1A1A] rounded-2xl p-6 mb-6 shadow-lg">
              <h2 className="text-white text-xl font-semibold mb-4">
                Deposit <span className="text-[#8BE68B]">CVT V1</span>
              </h2>

              {/* Coin Info Box */}
              <div className="bg-[#2B2B2B] rounded-xl p-4 mb-4 flex justify-between items-center">
                <div className="flex flex-col">
                  <CVTokenLogo2 />
                </div>
                <div className="text-right">
                  {balanceFailed ? (
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-gray-400 text-sm font-semibold">
                        Couldn't load balance
                      </span>
                      <button
                        onClick={handleRetry}
                        disabled={isRetrying}
                        className="text-[#00FF7F] text-sm font-bold underline underline-offset-2 disabled:opacity-60"
                      >
                        {isRetrying ? "Retrying..." : "Retry"}
                      </button>
                    </div>
                  ) : (
                    <div
                      className="text-white text-2xl font-bold"
                      title={`${cvt1Balance} CVT V1`}
                    >
                      {truncateDecimals(cvt1Balance, 2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Deposit Button */}
              <button
                onClick={handleDeposit}
                disabled={
                  isDepositing ||
                  balanceFailed ||
                  !hasPositiveBalance(cvt1Balance)
                }
                className="w-full font-semibold py-3 px-6 rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 text-black"
                style={{
                  background:
                    "linear-gradient(to right,rgba(59, 177, 78, 0.98),rgba(80, 177, 96, 0.98))",
                }}
              >
                {isDepositing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                    Depositing...
                  </>
                ) : (
                  "Deposit"
                )}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full flex items-stretch justify-center font-sora">
      <div className="w-full sm:max-w-md mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default HomePage;
